import { motion, AnimatePresence } from "framer-motion";
import { X, Fire, Snowflake, Check } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { myAxios } from "../utils/common";
import { IGamificationStats } from "../utils/queryOptions";

// Son 30 günü getir, hangi günlerde pratik yapılmış göster
function getLast30Days(): string[] {
  const days: string[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().split("T")[0]); // YYYY-MM-DD
  }
  return days;
}

function formatDay(dateStr: string): { day: string; weekday: string; isToday: boolean } {
  const d = new Date(dateStr + "T12:00:00");
  const today = new Date().toISOString().split("T")[0];
  const weekdays = ["Pz", "Pt", "Sa", "Ça", "Pe", "Cu", "Ct"];
  return {
    day: d.getDate().toString(),
    weekday: weekdays[d.getDay()],
    isToday: dateStr === today,
  };
}

export default function StreakCalendarModal({
  isOpen,
  onClose,
  stats,
}: {
  isOpen: boolean;
  onClose: () => void;
  stats: IGamificationStats;
}) {
  // Fetch practice dates + frozen days from backend
  const { data: calendarData } = useQuery({
    queryKey: ["streakCalendar"],
    queryFn: async () => {
      const res = await myAxios.get<{ dates: string[]; frozenDates: string[] }>(
        "/gamification/active-dates",
      );
      return {
        active: new Set(res.data.dates),
        frozen: new Set(res.data.frozenDates ?? []),
      };
    },
    staleTime: 60 * 1000,
    enabled: isOpen,
  });
  const activeDates = calendarData?.active;
  const frozenDates = calendarData?.frozen;

  const days = getLast30Days();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-surface-1 border border-white/[0.08] rounded-2xl p-6 max-w-md w-full relative"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-myOrange/20 rounded-2xl mb-3">
                <Fire size={32} weight="fill" className="text-myOrange" />
              </div>
              <h3 className="text-2xl font-bold">{stats.currentStreak} Gün Seri</h3>
              <p className="text-white/50 text-sm mt-1">
                En uzun seri: {stats.longestStreak} gün
              </p>
            </div>

            {/* Freeze info */}
            <div className="flex flex-col items-center gap-2 mb-5">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-sky-500/10 border border-sky-500/20 rounded-full px-3 py-1.5">
                  <Snowflake size={14} weight="fill" className="text-sky-400" />
                  <span className="text-xs font-bold text-sky-400">
                    {stats.freezeCount} Freeze
                  </span>
                </div>
                {stats.freezeInUse > 0 && (
                  <div className="flex items-center gap-1.5 bg-sky-500/20 border border-sky-500/40 rounded-full px-3 py-1.5">
                    <span className="text-xs font-bold text-sky-300">
                      {stats.freezeInUse} kullanımda
                    </span>
                  </div>
                )}
              </div>
              <div className="text-xs text-white/30">
                5 gün seri = 1 freeze kazanırsın
              </div>
            </div>

            {/* Calendar grid — last 30 days */}
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-xs text-white/40 mb-3 font-medium">Son 30 Gün</p>
              <div className="grid grid-cols-7 gap-1.5">
                {days.map((dateStr) => {
                  const { day, weekday, isToday } = formatDay(dateStr);
                  const isActive = activeDates?.has(dateStr) ?? false;
                  const isFrozen = !isActive && (frozenDates?.has(dateStr) ?? false);

                  return (
                    <div
                      key={dateStr}
                      title={
                        isActive
                          ? "Aktif"
                          : isFrozen
                            ? "Freeze ile korunuyor"
                            : undefined
                      }
                      className={`
                        aspect-square rounded-lg flex flex-col items-center justify-center text-center relative
                        ${isActive
                          ? "bg-myGreen/20 border border-myGreen/30"
                          : isFrozen
                            ? "bg-sky-500/15 border border-sky-500/40"
                            : "bg-white/5 border border-transparent"
                        }
                        ${isToday ? "ring-2 ring-myBlue/50" : ""}
                      `}
                    >
                      <span className="text-[9px] text-white/30">{weekday}</span>
                      <span
                        className={`text-xs font-bold ${
                          isActive
                            ? "text-myGreen"
                            : isFrozen
                              ? "text-sky-300"
                              : "text-white/40"
                        }`}
                      >
                        {day}
                      </span>
                      {isActive && (
                        <Check size={8} weight="bold" className="text-myGreen absolute bottom-0.5" />
                      )}
                      {isFrozen && (
                        <Snowflake size={8} weight="fill" className="text-sky-300 absolute bottom-0.5" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-3 mt-4 justify-center text-xs text-white/40 flex-wrap">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-myGreen/20 border border-myGreen/30" />
                <span>Aktif</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-sky-500/15 border border-sky-500/40" />
                <span>Freeze</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-white/5" />
                <span>Boş</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded ring-2 ring-myBlue/50" />
                <span>Bugün</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
